export {
  type BagReward,
  type RedemptionChannel,
  type RewardType,
} from '@sg/shared-fe-features-loyalty';

import {
  type CardExpirationState,
  type CardType,
  type Asset,
  type DeliveryOrderDetail,
  type Ingredient,
  type LineItem,
  type Product,
  type ProductLabel,
  type Restaurant,
  type OrderState,
} from '@sg/graphql-schema';

// ─── Upsell ──────────────────────────────────────────────────────────────────

export const PERSONALIZED_UPSELL_FLAG = 'personalized-upsells-v1';

export type UpsellPersonalization = 'control' | 'treatment';

export type UpsellItem = {
  id: string;
  name: string;
  cost: number;
  channelCost: number;
  imageUrl: string;
  outOfStock: boolean;
  menuId: number;
  isPersonalized?: boolean;
};

// ─── Customer ────────────────────────────────────────────────────────────────

export type OrderingCustomer = {
  customerId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  isNewCustomer: boolean;
  isOptedOutFromLoyalty: boolean;
};

// ─── Cart ────────────────────────────────────────────────────────────────────

export type OrderChannel = 'pickup' | 'delivery' | 'outpost';

export type OrderingCart = {
  orderId: string | undefined;
  orderState: OrderState;
  orderChannel: OrderChannel;
  wantedTimes: WantedTime[];
  dropoffLocations: readonly DropoffLocation[];
  locationName: string | undefined;
  restaurantId: string | undefined;
  restaurantEntityId: string | undefined;
  restaurantSlug: string | undefined;
  restaurantName: string | undefined;
  addressId: string | undefined;
  deliveryOrderDetail: DeliveryOrderDetail | undefined;
  deliveryPreferences: { type: string; notes: string };
  deliveryMinSubtotal: number | undefined;
  canTrackOrderStatus: boolean;
  isGroupOrder: boolean;
  groupOrderJoinCode: string | undefined;
  groupOrderHostName: string | undefined;
  canApplyRewards: boolean;
  canSubmitOrder: boolean;
  canUpdateOrderDetails: boolean;
  lineItems: readonly PartialLineItem[];
  ledger: OrderingLedger | undefined;
  appliedRewardId?: string;
  showDeliveryFeeDisclosure: boolean;
  showDeliveryPriceDifferenciationDisclosure: boolean;
  showOutpostPriceDifferenciationDisclosure: boolean;
  deliveryFee: number;
  maxTipAmount: number;
};

// ─── Ledger ──────────────────────────────────────────────────────────────────

export type OrderingLedger = {
  subtotal: number;
  feesTotal: number;
  discountsTotal: number;
  creditsTotal: number;
  tax: number;
  tip: number;
  discounts: ReadonlyArray<{
    name: string;
    amount: number;
    description: string | null;
  }>;
  credits: ReadonlyArray<{
    name: string;
    amount: number;
    description: string | null;
  }>;
  fees: ReadonlyArray<{
    name: string;
    amount: number;
    description: string | null;
  }>;
};

// ─── Wanted Time ─────────────────────────────────────────────────────────────

export type WantedTime = {
  time: string;
  offset: number;
};

// ─── Dropoff Locations ───────────────────────────────────────────────────────

export type DropoffLocation = {
  value: string;
  label: string;
};

// ─── Payment Methods ─────────────────────────────────────────────────────────

export type PaymentMethod = {
  id: string;
  description: string | null;
  nickname: string | null;
  isDefault: boolean;
  cardType: CardType;
  expirationState: CardExpirationState;
};

// ─── Line Items ──────────────────────────────────────────────────────────────

export type PartialProduct = Partial<
  Readonly<{
    label: Partial<ProductLabel> | null;
    restaurant: Partial<Restaurant>;
    ingredients: ReadonlyArray<Partial<PartialIngredient>>;
  }> &
    Omit<Product, 'restaurant' | 'ingredients' | 'label'>
>;

export type PartialIngredient = Partial<
  Readonly<{
    asset: Partial<Asset>;
  }> &
    Omit<Ingredient, 'asset'>
>;

export type PartialLineItem = Readonly<{
  product: Partial<PartialProduct>;
  ingredients?: ReadonlyArray<Partial<PartialIngredient>>;
  addedIngredients?: ReadonlyArray<Partial<PartialIngredient>>;
  removedIngredients?: ReadonlyArray<Partial<PartialIngredient>>;
}> &
  Partial<Omit<LineItem, 'product' | 'ingredients' | 'addedIngredients' | 'removedIngredients'>>;
